import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, AlertTriangle } from "lucide-react";

// Blurred full-screen backdrop with a centred card.
export function Modal({ isVisible, z = "z-[100]", className = "", children }) {
  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className={`fixed inset-0 ${z} flex items-center justify-center bg-slate-900/60 backdrop-blur-xl p-6 overflow-y-auto`}
        >
          <motion.div
            initial={{ scale: 0.9, y: 20, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            className={`bg-white shadow-[0_50px_100px_-20px_rgba(0,0,0,0.5)] border border-white w-full ${className}`}
          >
            {children}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// Card header for a dialog: tinted icon tile, title and a small caption.
export function DialogHeader({ icon: Icon, tone, title, caption }) {
  return (
    <div className="flex items-center gap-4 mb-8">
      <div className={`p-4 rounded-2xl ${tone.tile}`}>
        <Icon className={tone.icon} size={26} />
      </div>
      <div>
        <h3 className="text-2xl font-black tracking-tighter text-slate-900">{title}</h3>
        <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-widest">{caption}</p>
      </div>
    </div>
  );
}

export const DIALOG_TONES = {
  green: { tile: "bg-green-100", icon: "text-green-700" },
  amber: { tile: "bg-amber-100", icon: "text-amber-600" },
  red: { tile: "bg-red-100", icon: "text-red-600" },
};

// The pulsing success / failure overlay shown when a job ends.
export function ResultOverlay({ isVisible, success, title, children, buttonLabel, onClose, z }) {
  const tone = success
    ? { pulse: "bg-green-200", badge: "bg-green-600 shadow-green-200", hover: "hover:bg-green-600", Icon: CheckCircle2 }
    : { pulse: "bg-red-200", badge: "bg-red-600 shadow-red-200", hover: "hover:bg-red-600", Icon: AlertTriangle };
  return (
    <Modal isVisible={isVisible} z={z} className="rounded-[4rem] p-10 md:p-14 max-w-3xl text-center my-10">
      <div className="relative mb-8 inline-block">
        <motion.div
          animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0, 0.5] }}
          transition={{ repeat: Infinity, duration: 2 }}
          className={`absolute inset-0 rounded-full ${tone.pulse}`}
        />
        <div className={`relative w-24 h-24 rounded-full flex items-center justify-center text-white shadow-2xl ${tone.badge}`}>
          <tone.Icon size={48} strokeWidth={3} />
        </div>
      </div>
      <h2 className="text-5xl font-black text-slate-900 tracking-tighter mb-4">{title}</h2>
      <p className="text-slate-500 font-medium text-lg mb-8 break-words">{children}</p>
      <button
        onClick={onClose}
        className={`w-full py-6 bg-slate-900 text-white rounded-3xl font-black text-xs uppercase tracking-[0.3em] transition-all shadow-xl active:scale-95 ${tone.hover}`}
      >
        {buttonLabel}
      </button>
    </Modal>
  );
}

export const CatalogName = () => <span className="text-green-600 font-bold">NEED-Catalog</span>;
